import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerApi } from '../api/customers';
import { isApiError, refusalMessage } from '../api/errors';
import DriverPasswordField from '../components/driver/DriverPasswordField';
import { clearCustomerSession, getCustomerSession } from '../lib/customerSession';
import './driverLoginPremium.css';

/**
 * The phone's registration for delivery updates. At sign-in it was refused
 * behind the wall (403, which is never retried), so it is made again once the
 * password is chosen; it decides, and says, whether there is anything to do.
 */
function registerClientPhone() {
  void import('../lib/clientPush').then((m) => m.ensureClientPushRegistered()).catch(() => {});
}

/**
 * A client imported from the old app chooses their own password before
 * anything else opens (InGo D15: every old password was readable by anyone,
 * so the import asks again; the server walls every other client route until
 * then). The client's twin of `DriverChoosePasswordPage`: the same look, the
 * same field and the same rules, in the client's words. Behind
 * `CustomerAuthGate`; the old password is asked again, never carried between
 * screens.
 *
 * A client who does not know the old password (one who signed in with
 * Google) leaves signed out, for a reset link to their own address: any new
 * password lifts the wall. Signing out is open behind it.
 */
export default function ChoosePasswordPage({ customers = customerApi }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState({});
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setProblem('');
    if (password !== confirmation) {
      setErrors({ confirmation: 'The two passwords are not the same.' });
      return;
    }
    setBusy(true);
    try {
      await customers.choosePassword(current, password);
      registerClientPhone();
      navigate('/home', { replace: true });
    } catch (error) {
      const fields = isApiError(error) ? error.errors : {};
      if (fields.current_password || fields.password) {
        setErrors({ current: fields.current_password?.[0], password: fields.password?.[0] });
      } else {
        setProblem(refusalMessage(error));
      }
    } finally {
      setBusy(false);
    }
  };

  /** Signed out on the server and here, then on to `to`. */
  const leave = async (to) => {
    setBusy(true);
    await customers.signOut();
    clearCustomerSession();
    navigate(to, { replace: true });
  };

  const forgot = () => {
    const email = getCustomerSession()?.email ?? '';
    void leave(`/forgot-password?realm=customer${email ? `&email=${encodeURIComponent(email)}` : ''}`);
  };

  return (
    <div className="dp-login-page">
      <main className="dp-login-body dp-login-body--alone">
        <div className="dp-login-card">
          <form className="dp-login-form" onSubmit={submit}>
            <h1 className="dp-login-title">Choose your password</h1>
            <p className="dp-login-subtitle">Your password from the old InGo app works once. Choose your own to continue.</p>

            {problem ? (
              <p className="dp-login-error" role="alert">
                {problem}
              </p>
            ) : null}

            <DriverPasswordField
              id="ccp-current"
              label="Your old password"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              error={errors.current}
              disabled={busy}
            />
            <DriverPasswordField
              id="ccp-new"
              label="New password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              error={errors.password}
              disabled={busy}
            />
            <DriverPasswordField
              id="ccp-confirm"
              label="New password again"
              value={confirmation}
              onChange={setConfirmation}
              autoComplete="new-password"
              error={errors.confirmation}
              disabled={busy}
            />

            <button type="submit" className="dp-login-btn" disabled={busy}>
              {busy ? 'Saving…' : 'Save and continue'}
            </button>
          </form>

          <p className="dp-login-customer-line">
            <button type="button" className="dp-login-customer-link dp-login-link-btn" onClick={forgot} disabled={busy}>
              Forgot your old password?
            </button>
          </p>
          <p className="dp-login-customer-line">
            <button type="button" className="dp-login-customer-link dp-login-link-btn" onClick={() => void leave('/login')} disabled={busy}>
              Sign out
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
